/**
 * The signed-in user's own display fields. **There is still no `GET /users/me`** — what
 * changed under D139 is that `POST /auth/refresh` now returns the profile beside the tokens,
 * so this record is a *cache* rather than the only copy.
 *
 * It was written when it was the only copy: P2 handed the profile over exactly once, in the
 * `POST /auth/otp/verify` response, and the refresh returned tokens alone — so a device that
 * lost this row was asked for D42's name on every launch, with the answer already stored on
 * the server and no way to fetch it. That was logged as a nuisance and was in fact a dead end
 * (`implementation-notes.md` → P6, 2026-08-12).
 *
 * Two consequences for anything reading this module. **A failed write is survivable** — the
 * next refresh rebuilds the row, which is why `SessionManager` warns rather than throwing on
 * one. And **the server wins**: `#adoptRefreshedProfile` overwrites what is here, adopting a
 * `name` when the response carries one and leaving this value alone when it does not.
 *
 * `device-cache.md`'s cloud-owned table already permits this: *"User self-name → Name needed
 * by the current surface"*, with the profile response winning on reconciliation. It lives in
 * the **derived** `snapshots` store, so a schema change discards it and the next sign-in,
 * refresh, or `PATCH /users/me` rebuilds it.
 */

import type { KeyValueStore } from './store'
import type { Locale } from '../i18n/locales'

export interface CachedProfile {
  readonly userId: string
  readonly name?: string
  readonly locale: Locale
}

export const profileKey = (stage: string, userId: string): string => `profile:${stage}:${userId}`

export async function readProfile(
  store: KeyValueStore,
  stage: string,
  userId: string,
): Promise<CachedProfile | undefined> {
  const record = await store.get<CachedProfile>('snapshots', profileKey(stage, userId))
  if (record === undefined || record.userId !== userId) return undefined
  return record
}

export async function writeProfile(
  store: KeyValueStore,
  stage: string,
  profile: CachedProfile,
): Promise<void> {
  await store.put('snapshots', profileKey(stage, profile.userId), profile)
}
